import 'client-only';

import CheckmarkIcon from '@curology/ui-components-web/react/Icon/icons/CheckmarkIcon';
import { useAtom } from 'jotai';

import { FlashMessagesState } from '@/atoms/flashMessages/flashMessagesState';
import { useDataProvider } from '@/components/flow/components/DataProvider/useDataProvider';
import { DATA_KEYS } from '@/consts/dataKeys';
import { type SelectableOption } from '@/types/productSelectionAndCheckout/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

import { useOtcCards } from './useOtcCards';
import { useProductIngredientsModalState } from './useProductIngredientsModalState';
import { useSelectedItemIds } from './useSelectedItemIds';
import { useSubmittingForm } from './useSubmittingForm';

export const useChangeSelectableOption = () => {
  const { isCardSelected, updateSelectedItemIds } = useSelectedItemIds();
  const { swapOtcCardSelectableOption } = useOtcCards();
  const { patient } = useDataProvider(DATA_KEYS.productSelection);
  const { submittingForm } = useSubmittingForm();
  const [flashMessages, setFlashMessages] = useAtom(
    FlashMessagesState.flashMessagesAtom,
  );
  const { setModalCard } = useProductIngredientsModalState();

  return (selectableOption: SelectableOption) => {
    if (submittingForm) {
      return;
    }

    const result = swapOtcCardSelectableOption?.(selectableOption);

    if (!result) {
      return;
    }

    const { oldCard, newCard } = result;

    setModalCard(newCard);

    if (isCardSelected?.(oldCard)) {
      updateSelectedItemIds?.(
        { ids: oldCard.fullSizeItemId!, mode: 'remove' },
        { ids: newCard.fullSizeItemId!, mode: 'add' },
      );
    }

    setFlashMessages([
      ...flashMessages,
      {
        icon: <CheckmarkIcon />,
        iconSize: 'sm',
        state: 'success',
        title: 'Product option selected',
      },
    ]);

    trackEvent(AnalyticsEvent.PRODUCT_OPTION_SELECTED, {
      selectableOption: selectableOption.name,
      offerId: patient?.introOffer?.id,
    });
  };
};
